import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import { CONFIG_ITEM_KEYS } from '../../../config-items/config-item-keys';
import type { EditorPluginAIConfigItemMarkdownAction } from '../../../config-items/config-items';
import { createEditorPluginAIConfigItemMarkdown } from '../../../config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../../config-items/slice-or-node-to-markdown';
import { aiSpellingGrammarPluginKey } from '../../../pm-plugins/ai-spelling-grammar/ai-spelling-grammar-plugin-key';
import { handleSpellingAndGrammarWithAnalytics } from '../../../pm-plugins/ai-spelling-grammar/commands-with-analytics';
import { getConfiguration as getProactiveAIConfig } from '../../../pm-plugins/ai-spelling-grammar/configuration';
import { getAllDiffObjects } from '../../../pm-plugins/ai-spelling-grammar/utils';
import { streamConvoAI } from '../../../provider/prompt-requests/convo-ai';
import type {
	BackendModel,
	GrammarSpellingIntentSchema,
	ImproveQualityIntentSchema,
} from '../../../provider/prompt-requests/types';
import { streamXPGenAI } from '../../../provider/prompt-requests/xp-gen-ai';
import { Icon as FixSpellingIcon } from '../../assets/icons/fixSpelling';
import { Icon as ImproveWritingIcon } from '../../assets/icons/improveWriting';
import type { IconShownAt } from '../../assets/icons/types';
import { createInsertBelow, createReplace } from '../../config-item-actions/markdown';
import { isMinLength } from '../../config-item-visible/visibility-utils';
import { getSlice } from '../utils';

import { messages } from './messages';

const SELECTION_TYPE = 'range';

function createEnhanceOption({
	key,
	title,
	description,
	actions,
	intentSchemaId,
	icon,
	getBackendModel,
	onSelectForPrompt,
}: {
	key: CONFIG_ITEM_KEYS;
	title: MessageDescriptor;
	description: MessageDescriptor;
	actions: EditorPluginAIConfigItemMarkdownAction[];
	intentSchemaId: GrammarSpellingIntentSchema | ImproveQualityIntentSchema;
	icon?: ({
		formatMessage,
		shownAt,
	}: {
		formatMessage: IntlShape['formatMessage'];
		shownAt: IconShownAt;
	}) => JSX.Element;
	getBackendModel: (isInterrogate?: boolean) => BackendModel;
	onSelectForPrompt?: ({
		context,
		editorView,
		endExperience,
	}: {
		context: any;
		editorView: EditorView;
		endExperience: () => void;
	}) => void;
}) {
	return createEditorPluginAIConfigItemMarkdown({
		key,
		title,
		description,
		selectionType: SELECTION_TYPE,
		getInitialContext: ({ editorView, positions, intl, updateIdMap, mentionMap }) => {
			const selection = sliceOrNodeToMarkdown({
				slice: getSlice(editorView, positions),
				intl,
				editorView,
				convertTo: 'markdown-plus',
				updateIdMap,
				selectionType: SELECTION_TYPE,
				mentionMap,
			});

			const document = sliceOrNodeToMarkdown({
				node: editorView.state.doc,
				intl,
				editorView,
				convertTo: 'markdown-plus',
				updateIdMap,
				selectionType: SELECTION_TYPE,
				mentionMap,
			});

			return {
				selection: selection.markdown,
				document: document.markdown,
				userLocale: intl.locale,
				intentSchemaId,
				contentStatistics: selection.contentStatistics,
			};
		},
		triggerPromptRequest({ initialContext, editorSchema }) {
			if (
				intentSchemaId === 'improve_quality_intent_schema.json' &&
				fg('platform.editor.ai-assistance-service-improve-writing')
			) {
				return streamConvoAI({
					userLocale: initialContext.userLocale,
					intentSchemaId: 'improve_quality_intent_schema.json',
					editorSchema,
					fullDocument: initialContext.document,
					currentSelection: initialContext.selection,
				});
			}

			if (
				intentSchemaId === 'grammar_spelling_intent_schema.json' &&
				fg('platform.editor.ai-assistance-service-fix-spelling-grammar')
			) {
				return streamConvoAI({
					userLocale: initialContext.userLocale,
					intentSchemaId: 'grammar_spelling_intent_schema.json',
					editorSchema,
					fullDocument: initialContext.document,
					currentSelection: initialContext.selection,
				});
			}

			return streamXPGenAI({
				userLocale: initialContext.userLocale,
				intentSchemaId,
				editorSchema,
				contextList: [
					{
						type: 'ADF_MARKDOWN_V1',
						entity: initialContext.selection,
						relationship: 'SELECTION',
					},
				],
			});
		},
		isVisible: ({ editorView, positions }) => isMinLength({ editorView, minLength: 1, positions }),
		icon,
		actions,
		getBackendModel,
		onSelectForPrompt,
	});
}

export const improveWriting = createEnhanceOption({
	key: CONFIG_ITEM_KEYS.IMPROVE_WRITING,
	title: messages.improveWritingTitle,
	description: messages.improveWritingDescription,
	actions: [
		createInsertBelow({ appearance: 'secondary' }),
		createReplace({ appearance: 'primary' }),
	],
	intentSchemaId: 'improve_quality_intent_schema.json',
	icon: ({ shownAt }) => <ImproveWritingIcon shownAt={shownAt} />,
	getBackendModel: () => {
		if (fg('platform.editor.ai-assistance-service-improve-writing')) {
			return 'assistance-service';
		}
		return 'xp-gen-ai';
	},
});

export const fixSpellingAndGrammar = createEnhanceOption({
	key: CONFIG_ITEM_KEYS.FIX_SPELLING_AND_GRAMMAR,
	title: messages.fixSpellingGrammarTitle,
	description: messages.fixSpellingGrammarDescription,
	actions: [createReplace({ appearance: 'primary' })],
	intentSchemaId: 'grammar_spelling_intent_schema.json',
	icon: ({ shownAt }) => <FixSpellingIcon shownAt={shownAt} />,
	getBackendModel: () => {
		if (fg('platform.editor.ai-assistance-service-fix-spelling-grammar')) {
			return 'assistance-service';
		}
		return 'xp-gen-ai';
	},
	onSelectForPrompt: ({ context, editorView, endExperience }) => {
		const { isProactiveAISupported } = getProactiveAIConfig(context.aiProvider);

		const fullDocumentSelection =
			context.positions[0] === 1 && context.positions[1] === editorView.state.doc.content.size - 1;

		if (
			isProactiveAISupported &&
			(context.positions[0] === context.positions[1] || fullDocumentSelection)
		) {
			const aiSpellingGrammarPluginState = aiSpellingGrammarPluginKey.getState(editorView.state);
			const { toggleCount, dismissedWords, insertionCount, initialToggleState } =
				aiSpellingGrammarPluginState;

			const allDiffObjects = getAllDiffObjects(aiSpellingGrammarPluginState);

			handleSpellingAndGrammarWithAnalytics({
				proactiveToggleCount: toggleCount + 1,
				totalSuggestionsOnPage: allDiffObjects.length,
				dismissedWords: dismissedWords.size,
				insertionCount: insertionCount,
				initialToggleState,
				triggeredFrom: 'aiCommandPalette',
				operationType: 'start',
			})(editorView.state, editorView.dispatch);
			endExperience();
		}
	},
});
